import React from 'react';
import { FaHeart, FaBrain, FaLeaf, FaStar } from 'react-icons/fa';
import { motion } from 'framer-motion';

const ApproachToWellnessSection = () => {
  const heroFeatures = [
    {
      icon: FaHeart,
      title: "Holistic Healing",
      description: "Comprehensive approach to wellness",
      color: "from-red-500 to-pink-500"
    },
    {
      icon: FaBrain,
      title: "Advanced Technology",
      description: "EU-certified medical devices",
      color: "from-blue-500 to-purple-500"
    },
    {
      icon: FaLeaf,
      title: "Natural Methods",
      description: "Non-invasive therapeutic approaches",
      color: "from-green-500 to-emerald-500"
    }
  ];

  const stats = [
    { number: "15+", label: "Specialized Services" },
    { number: "6000+", label: "Happy Clients" },
    { number: "9+", label: "Years Experience" }
  ];

  return (
    <div className="relative w-full bg-gradient-to-br from-white via-yellow/5 to-gold/5">
      {/* Our Approach to Wellness Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4"
              >
                <FaStar className="mr-2" />
                Why Choose Us
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-4xl font-bold text-dark-grey mb-4"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Our Approach to Wellness
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-lg text-gray-600"
              >
                Experience the difference with our comprehensive holistic approach
              </motion.p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {heroFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                  className="group relative"
                >
                  <div className="relative p-6">
                    <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-grey/20 h-full relative overflow-hidden group-hover:scale-105">
                      <div className="relative z-10">
                        <div className="w-20 h-20 bg-gradient-to-r from-gold/10 via-transparent to-blue/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-all duration-500 shadow-lg relative">
                          <feature.icon className="text-3xl text-gold" />
                          <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10 rounded-full blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
                        </div>
                        <h3 className="text-xl font-semibold text-dark-grey mb-3 text-center group-hover:text-gold transition-colors duration-300">{feature.title}</h3>
                        <p className="text-gray-600 text-center leading-relaxed">{feature.description}</p>
                      </div>
                    </div>
                    <div className="absolute inset-0 border-4 border-gold/40 rounded-2xl transform rotate-2 group-hover:rotate-0 transition-transform duration-700 shadow-xl"></div>
                    <div className="absolute inset-3 border-2 border-white/60 rounded-2xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                    <div className="absolute inset-6 border border-gold/20 rounded-2xl transform rotate-1 group-hover:rotate-0 transition-transform duration-300"></div>
                  </div>
                  <div className="absolute top-6 right-6 w-3 h-3 bg-gradient-to-r from-gold to-yellow rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100 shadow-lg animate-pulse"></div>
                  <div className="absolute bottom-6 left-6 w-2 h-2 bg-gradient-to-r from-blue to-purple rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200 shadow-lg animate-pulse"></div>
                  <div className="absolute top-1/2 right-4 w-1 h-1 bg-gradient-to-r from-green to-emerald rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-300 shadow-lg animate-pulse"></div>
                  <div className="absolute -top-2 -right-2 w-6 h-6 border-2 border-gold/30 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-400"></div>
                  <div className="absolute -bottom-2 -left-2 w-4 h-4 border-2 border-blue/30 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-500"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-gold/10 via-blue/10 to-gold/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                  className="text-center group"
                >
                  <div className="relative p-4">
                    <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-grey/20 relative overflow-hidden group-hover:scale-110">
                      <div className="absolute inset-0 bg-gradient-to-br from-gold/5 via-transparent to-blue/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      <div className="absolute inset-0 bg-gradient-to-r from-gold to-blue opacity-0 group-hover:opacity-15 transition-opacity duration-700 blur-xl"></div>
                      <div className="relative z-10">
                        <motion.div
                          animate={{
                            scale: [1, 1.05, 1],
                            textShadow: [
                              "0 0 0px rgba(184, 139, 68, 0)",
                              "0 0 20px rgba(184, 139, 68, 0.5)",
                              "0 0 0px rgba(184, 139, 68, 0)"
                            ]
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                          className="text-5xl font-bold text-gold mb-2 group-hover:scale-110 transition-transform duration-500 relative"
                        >
                          {stat.number}
                          <div className="absolute inset-0 bg-gradient-to-r from-gold to-yellow blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
                        </motion.div>
                        <div className="text-lg text-dark-grey font-semibold group-hover:text-gold transition-colors duration-300">
                          {stat.label}
                        </div>
                      </div>
                    </div>
                    <div className="absolute inset-0 border-4 border-gold/40 rounded-2xl transform rotate-1 group-hover:rotate-0 transition-transform duration-700 shadow-xl"></div>
                    <div className="absolute inset-3 border-2 border-white/60 rounded-2xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                    <div className="absolute inset-6 border border-gold/20 rounded-2xl transform rotate-1 group-hover:rotate-0 transition-transform duration-300"></div>
                  </div>
                  <div className="absolute top-4 right-4 w-3 h-3 bg-gradient-to-r from-gold to-yellow rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100 shadow-lg animate-pulse"></div>
                  <div className="absolute bottom-4 left-4 w-2 h-2 bg-gradient-to-r from-blue to-purple rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200 shadow-lg animate-pulse"></div>
                  <div className="absolute -top-2 -right-2 w-6 h-6 border-2 border-gold/30 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-300"></div>
                  <div className="absolute -bottom-2 -left-2 w-4 h-4 border-2 border-blue/30 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-400"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ApproachToWellnessSection;
